import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import logoIcon from 'assets/common/icons/logo_l.png';
import { COPY_RIGHT } from 'shared/constants/ui';
import { fetchMe, logoutWithSession } from 'shared/api/sessionAuth';

import 'styles/layout.css';

interface LayoutProps {
  children: React.ReactNode;
}

// ハンバーガーメニューアイコン（アニメーション付き）
const HamburgerIcon: React.FC<{ open: boolean; onClick: () => void }> = ({ open, onClick }) => (
  <button
    className="relative flex items-center justify-center w-8 h-8 focus:outline-none md:hidden"
    aria-label={open ? 'メニューを閉じる' : 'メニューを開く'}
    onClick={onClick}
    type="button"
  >
    {/* 横3本線 or × */}
    <span
      className={`block absolute h-0.5 w-6 bg-gray-700 rounded transition-all duration-300 ease-in-out
        ${open ? 'rotate-45 top-4' : 'top-2'}
      `}
    />
    <span
      className={`block absolute h-0.5 w-6 bg-gray-700 rounded transition-all duration-300 ease-in-out
        ${open ? 'opacity-0' : 'top-4'}
      `}
    />
    <span
      className={`block absolute h-0.5 w-6 bg-gray-700 rounded transition-all duration-300 ease-in-out
        ${open ? '-rotate-45 top-4' : 'top-6'}
      `}
    />
  </button>
);

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  // bodyの背景色をTailwind CSSのクラスで設定
  useEffect(() => {
    document.body.classList.add('bg-background');
    return () => {
      document.body.classList.remove('bg-background');
    };
  }, []);

  const [userName, setUserName] = useState('No Name');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const me = await fetchMe();
        if (mounted) {
          setUserName(me.full_name || me.username);
        }
      } catch {
        if (mounted) {
          setUserName('No Name');
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const handleLogout = async () => {
    try {
      await logoutWithSession();
    } finally {
      navigate('/login');
    }
  };

  const isLoginPage = location.pathname === '/login';

  return (
    <div className="layout">
      {/* Header（ログイン画面以外で表示） */}
      {!isLoginPage && (
        <header className="sticky top-0 z-20 flex items-center justify-between flex-shrink-0 h-12 px-4 bg-white border-b border-gray-200">
          {/* ロゴ（中央寄せ、モバイル時は絶対中央） */}
          <div className="relative flex justify-center flex-1 md:justify-start">
            <button
              className="focus:outline-none"
              onClick={() => navigate('/')}
              aria-label="ホームへ"
              type="button"
              style={{ background: 'none', border: 'none', padding: 0 }}
            >
              <img className="object-contain h-8" src={logoIcon} alt="Goodrescue24" />
            </button>
          </div>

          {/* 右側：ユーザー名とログアウト（md以上で表示、モバイルは非表示） */}
          <div className="items-center hidden space-x-4 md:flex">
            <span className="text-sm text-gray-700">{userName} 様</span>
            <button
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-primary rounded-full shadow transition-colors duration-150 bg-transparent hover:bg-primary hover:text-white active:shadow-inner"
              onClick={handleLogout}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H7a2 2 0 01-2-2V7a2 2 0 012-2h4a2 2 0 012 2v1" />
              </svg>
              ログアウト
            </button>
          </div>

          {/* ハンバーガー（モバイルのみ右端） */}
          <div className="flex items-center md:hidden">
            <HamburgerIcon open={menuOpen} onClick={() => setMenuOpen(!menuOpen)} />
          </div>
        </header>
      )}

      {/* ハンバーガーメニュー内容（モバイル時のみ右上にアニメーションで表示） */}
      {!isLoginPage && (
        <div
          className={`fixed z-30 top-12 right-2 md:hidden transition-all duration-300 ${
            menuOpen
              ? 'opacity-100 pointer-events-auto translate-y-0'
              : 'opacity-0 pointer-events-none -translate-y-4'
          }`}
          style={{ width: '14rem' }} // w-64
        >
          <nav className="bg-white border border-gray-200 rounded-md shadow">
            <ul className="flex flex-col items-stretch gap-2 py-4">
              <li className="px-6 text-sm text-center text-gray-700">{userName} 様</li>
              <li className="px-6">
                <button
                  className="flex items-center justify-center w-full gap-1 px-3 py-2 text-xs font-semibold transition-colors duration-150 bg-transparent rounded-full shadow text-primary hover:bg-primary hover:text-white active:shadow-inner"
                  onClick={() => {
                    setMenuOpen(false);
                    handleLogout();
                  }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H7a2 2 0 01-2-2V7a2 2 0 012-2h4a2 2 0 012 2v1" />
                  </svg>
                  ログアウト
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}

      {/* Main Content */}
      <main className="flex justify-center flex-1">
        <div className="w-[90%] md:w-[60%]">
          {children}
        </div>
      </main>

      {/* Footer（ログイン画面以外で表示） */}
      {!isLoginPage && (
        <footer className="flex items-center justify-center flex-shrink-0 h-12 bg-gray-100">
          <p className="text-xs text-center text-gray-400">
            &copy; {COPY_RIGHT}
          </p>
        </footer>
      )}
    </div>
  );
};

export default Layout;
